import React from 'react';
import { motion } from 'framer-motion';
import { DesignIcon, TransformIcon, ScaleIcon, SupportIcon, AIIcon } from './FeatureIcons';

const About: React.FC = () => {
  const features = [
    {
      title: 'Multi-Agent Orchestration',
      description: 'Build sophisticated multi-agent systems that coordinate, delegate, and execute complex workflows. Parallel execution, task decomposition, and intelligent routing across specialized agents.',
      icon: AIIcon,
      gradient: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)'
    },
    {
      title: 'Context Management & Memory',
      description: 'Advanced context window optimization, intelligent summarization, and persistent memory systems. Keep agents coherent across long sessions and multi-turn conversations.',
      icon: DesignIcon,
      gradient: 'linear-gradient(135deg, #b24bf3 0%, #9333ea 100%)'
    },
    {
      title: 'Agentic Dynamic Workflows',
      description: 'Self-adapting workflows that fan out, verify, and synthesize. Loop-until-dry patterns, adversarial verification, and quality gates that ensure correctness at scale.',
      icon: TransformIcon,
      gradient: 'linear-gradient(135deg, #00ffaa 0%, #10b981 100%)'
    },
    {
      title: 'LLM Infrastructure & Fine-tuning',
      description: 'Production-grade LLM infrastructure with Claude, GPT-4, and custom models. Prompt caching, structured outputs, tool use, and model-specific optimizations.',
      icon: ScaleIcon,
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
    },
    {
      title: 'AI System Architecture',
      description: 'Design end-to-end AI systems from retrieval pipelines to agent frameworks. RAG implementations, vector databases, embeddings, and semantic search at scale.',
      icon: SupportIcon,
      gradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)'
    }
  ];

  return (
    <section id="about" style={{
      padding: '8rem 0',
      background: 'linear-gradient(180deg, #000000 0%, #0a0e27 50%, #000000 100%)',
      position: 'relative'
    }}>
      <div style={{ maxWidth: '72rem', margin: '0 auto', padding: '0 1.5rem' }}>

        {/* Header */}
        <motion.div
          style={{ textAlign: 'center', marginBottom: '5rem' }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p style={{
            color: '#00d4ff',
            fontWeight: '600',
            fontSize: '0.875rem',
            marginBottom: '1rem',
            textTransform: 'uppercase',
            letterSpacing: '0.1em'
          }}>
            Core Capabilities
          </p>
          <h2 style={{
            fontSize: 'clamp(2.25rem, 4vw, 3rem)',
            fontWeight: 'bold',
            color: '#ffffff',
            marginBottom: '1.5rem',
            lineHeight: '1.2'
          }}>
            Advanced AI Agent Systems
            <br />
            <span style={{
              background: 'linear-gradient(135deg, #00d4ff 0%, #b24bf3 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Built for Scale
            </span>
          </h2>
          <p style={{
            fontSize: '1.25rem',
            color: 'rgba(255, 255, 255, 0.7)',
            maxWidth: '48rem',
            margin: '0 auto',
            lineHeight: '1.75'
          }}>
            We architect intelligent agent systems that think, coordinate, and execute.
            From multi-agent orchestration to context management at scale.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '2rem'
        }}>
          {features.map((feature, index) => (
            <motion.div
              key={`feature-${index}`}
              style={{
                background: 'rgba(255, 255, 255, 0.03)',
                padding: '2.5rem',
                borderRadius: '1rem',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                transition: 'all 300ms ease',
                position: 'relative',
                overflow: 'hidden'
              }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              whileHover={{
                y: -8,
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(0, 212, 255, 0.3)',
                boxShadow: '0 20px 60px -10px rgba(0, 212, 255, 0.3)'
              }}
            >
              {/* Gradient accent bar */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: feature.gradient
              }} />

              <div style={{ marginBottom: '1.5rem', marginTop: '0.5rem' }}>
                <feature.icon size={48} />
              </div>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#ffffff',
                marginBottom: '1rem'
              }}>
                {feature.title}
              </h3>
              <p style={{
                fontSize: '1rem',
                color: 'rgba(255, 255, 255, 0.7)',
                lineHeight: '1.75'
              }}>
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;
